import type { Celda, Cuadro } from '@domain/rules/cuentas.ts'

/**
 * La cuenta tal como queda en el cuaderno.
 *
 * Una rejilla de celdas del mismo ancho, con las llevadas pequeñas encima, el
 * signo a la izquierda y la raya donde empieza el resultado. La división lleva
 * su caja: el dividendo a la izquierda, el divisor a la derecha con su ángulo,
 * y el cociente debajo del divisor, que es donde lo escribe un maestro de aquí.
 *
 * Lo escrito en el paso actual va en el color del niño. No hay animación: lo
 * que hay que ver es dónde ha aparecido cada cifra, y para eso basta el color.
 */

function CeldaView({ celda, color, pequena }: { celda: Celda; color: string; pequena?: boolean }) {
  return (
    <span
      className={`inline-flex items-center justify-center tabular-nums ${
        pequena ? 'h-5 w-8 text-sm' : 'h-10 w-8 text-3xl'
      } ${celda.nuevo ? 'font-extrabold' : ''} ${celda.tenue ? 'text-muted' : ''}`}
      style={celda.nuevo ? { color } : undefined}
    >
      {celda.t}
    </span>
  )
}

export function CuadroView({ cuadro, color }: { cuadro: Cuadro; color: string }) {
  if (cuadro.tipo === 'nada') return null

  if (cuadro.tipo === 'tabla') {
    const filas = Array.from({ length: cuadro.pide }, (_, i) => i + 1)
    return (
      <div
        data-testid="cuadro"
        className="border-rule bg-surface mx-auto grid w-fit gap-0 rounded-2xl border px-6 py-3 text-xl tabular-nums"
      >
        {filas.map((por) => {
          const pide = por === cuadro.pide
          return (
            <p
              key={por}
              className={`m-0 ${pide ? 'font-extrabold' : por > cuadro.hasta ? 'text-muted' : ''}`}
              style={pide ? { color } : undefined}
            >
              {cuadro.tabla} × {por} = {pide ? '?' : cuadro.tabla * por}
            </p>
          )
        })}
      </div>
    )
  }

  if (cuadro.tipo === 'caja') {
    const ancho = Math.max(cuadro.dividendo.length, ...cuadro.restos.map((r) => r.length))
    return (
      <div
        data-testid="cuadro"
        className="border-rule bg-surface mx-auto flex w-fit items-start rounded-2xl border px-6 py-4"
      >
        <div className="grid" style={{ gridTemplateColumns: `repeat(${ancho}, 2rem)` }}>
          {cuadro.dividendo.map((celda, i) => (
            <CeldaView key={`d${i}`} celda={celda} color={color} />
          ))}
          {cuadro.restos.map((fila, r) =>
            Array.from({ length: ancho }, (_, i) => (
              <CeldaView key={`r${r}-${i}`} celda={fila[i] ?? { t: '' }} color={color} />
            )),
          )}
        </div>
        <div className="border-ink ml-2 border-l-2 pl-2">
          <div className="border-ink flex border-b-2">
            {cuadro.divisor.map((celda, i) => (
              <CeldaView key={`v${i}`} celda={celda} color={color} />
            ))}
          </div>
          <div className="flex">
            {cuadro.cociente.map((celda, i) => (
              <CeldaView key={`c${i}`} celda={celda} color={color} />
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div
      data-testid="cuadro"
      className="border-rule bg-surface mx-auto w-fit rounded-2xl border px-6 py-3"
    >
      {cuadro.filas.map((fila, f) => (
        <div
          key={f}
          className={`flex items-center ${fila.raya ? 'border-ink border-t-2' : ''}`}
          data-papel={fila.papel}
        >
          <span
            className={`inline-flex w-8 items-center justify-center text-3xl ${
              fila.papel === 'llevadas' ? 'h-5' : 'h-10'
            }`}
          >
            {fila.signo ?? ''}
          </span>
          {fila.celdas.map((celda, i) => (
            <CeldaView key={i} celda={celda} color={color} pequena={fila.papel === 'llevadas'} />
          ))}
        </div>
      ))}
    </div>
  )
}
